import { adminHttp } from "@shared/lib/http";
import { isStubEnabled } from "@sdk/core";
import { translateUi } from "@i18n/config";
import type { NavigationSettings } from "@shared/config/navigation";

const KEY = "storizone:settings:v1";

export type GeneralSettings = {
  storeName: string;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  supportEmail?: string;
  supportPhone?: string;
  whatsAppNumber?: string;
  primaryColor?: string;
  address?: string;
  mapUrl?: string;
};

export type HeaderSettings = {
  enabled: boolean;
  showSearch: boolean;
  showWishlist: boolean;
  showCart: boolean;
  showAccount: boolean;
  announcementEnabled: boolean;
  announcementPhone?: string;
  announcementEmail?: string;
  announcementText?: string;
  announcementButtonText?: string;
  announcementButtonUrl?: string;
};

export type ServiceHighlightSettings = {
  enabled: boolean;
  icon: string;
  title: string;
  subtitle?: string;
};

export type HeroSlideSettings = {
  enabled: boolean;
  title: string;
  copy: string;
  badge: string;
  ctaText?: string;
  href: string;
  imageUrl?: string;
};

export type PartnerLogoSettings = {
  enabled: boolean;
  imageUrl: string;
  alt?: string;
  href?: string;
};

export type FeaturedDealSettings = {
  enabled: boolean;
  title?: string | null;
  description?: string | null;
  ctaText?: string | null;
  href?: string | null;
  imageUrl?: string | null;
};

export type RecentOrdersSettings = {
  enabled: boolean;
  maxProducts: number | null;
  title?: string | null;
  subtitle?: string | null;
};

export type StorefrontDesignSettings = {
  preset: "modern" | "editorial" | "minimal" | "luxury" | "playful" | "bold";
  secondaryColor?: string | null;
  accentColor?: string | null;
  backgroundColor: string;
  foregroundColor: string;
  headingFont: string;
  bodyFont: string;
  contentWidth: "standard" | "wide" | "full";
  sectionSpacing: "compact" | "comfortable" | "spacious";
  buttonStyle: "square" | "rounded" | "pill";
  cardStyle: "soft" | "bordered" | "minimal";
  imageRatio: "square" | "portrait" | "landscape";
  animationLevel: "none" | "subtle" | "playful";
  mobileProductColumns: 1 | 2;
};

export type HomeSettings = {
  heroEnabled: boolean;
  heroSlides: HeroSlideSettings[];
  categoriesEnabled: boolean;
  categoriesTitle?: string;
  categoriesButtonText?: string;
  categoriesButtonUrl?: string;
  serviceHighlightsEnabled: boolean;
  serviceHighlights: ServiceHighlightSettings[];
  partnerLogosEnabled: boolean;
  partnerLogosTitle?: string;
  partnerLogos: PartnerLogoSettings[];
  featuredDeal?: FeaturedDealSettings | null;
  recentOrders?: RecentOrdersSettings | null;
  sections?: PageSection[];
  design?: StorefrontDesignSettings | null;
  schemaVersion?: number;
};

export type PageSection = {
  id: string;
  type: string;
  data: any;
  enabled: boolean;
  translations?: Record<string, any>;
};

export type LocalizationSettings = {
  defaultCurrency: string;
  supportedCurrencies: string[];
  defaultLanguage: string;
  languages: string[];
  timezone: string;
};

export type CheckoutSettings = {
  allowGuestCheckout: boolean;
  requirePhone: boolean;
  defaultCountry?: string;
  allowCountrySelection: boolean;
  requireLastName: boolean;
  requireAddressLine1: boolean;
  requireCity: boolean;
  requireRegion: boolean;
  requirePostalCode: boolean;
  whatsAppCartEnabled: boolean;
  zeroPriceInquiryEnabled?: boolean;
  orderEnquiriesOnly?: boolean;
  taxEnabled: boolean;
  shippingEnabled: boolean;
  bankInfo?: string | null;
  bankAccountName?: string | null;
  bankAccountNumber?: string | null;
  bankName?: string | null;
  bankIban?: string | null;
  bankSwift?: string | null;
  enableCard: boolean;
  enableCod: boolean;
  enableBankTransfer: boolean;
};

export type CustomerAccountsSettings = {
  passwordLoginEnabled: boolean;
  registrationEnabled: boolean;
  googleLoginEnabled: boolean;
};

export type EmailSettings = {
  fromName: string;
  fromEmail: string;
  sendOrderConfirmation: boolean;
  sendAdminNotification: boolean;
};

export type NewsletterSectionSettings = {
  enabled: boolean;
  title?: string;
  subtitle?: string;
  buttonLabel?: string;
  marketingConsentText?: string | null;
  marketingConsentDefault?: boolean;
};

export type TrackingPixelPlatform =
  | "facebook"
  | "twitter"
  | "linkedin"
  | "pinterest"
  | "quora"
  | "bing"
  | "google-analytics"
  | "google-adwords"
  | "snapchat"
  | "tiktok";

export type TrackingPixelSettings = {
  platform: TrackingPixelPlatform | string;
  pixelId: string;
  enabled: boolean;
};

export type SocialLinkPlatform =
  | "facebook"
  | "x"
  | "instagram"
  | "youtube"
  | "pinterest"
  | "linkedin"
  | "tiktok";

export type SocialLinkSettings = {
  platform: SocialLinkPlatform | string;
  url: string;
  enabled: boolean;
};

export type FooterSettings = {
  enabled: boolean;
  description?: string;
  contactEnabled: boolean;
  contactTitle?: string;
  copyrightEnabled: boolean;
  copyrightText?: string;
  paymentIcons: PaymentIconSettings[];
};

export type PaymentIconSettings = {
  url: string;
  alt?: string;
  enabled: boolean;
};

export type MarketingSettings = {
  newsletter: NewsletterSectionSettings;
  pixels: TrackingPixelSettings[];
  socialLinks: SocialLinkSettings[];
};

export type PwaSettings = {
  enabled: boolean;
  name: string;
  shortName: string;
  description?: string;
  startUrl: string;
  display: "standalone" | "fullscreen" | "minimal-ui" | "browser";
  themeColor: string;
  backgroundColor: string;
  icon192Url?: string;
  icon512Url?: string;
  offlinePageContent?: string;
  pushEnabled?: boolean;
  vapidPublicKey?: string | null;
  vapidPrivateKey?: string | null;
};

export type BrowserNotificationsSettings = {
  enabled: boolean;
  transactionalEnabled: boolean;
  promotionalEnabled: boolean;
  promptEnabled: boolean;
  promptTitle: string;
  promptMessage: string;
  promptDelaySeconds: number;
  promptDismissalDays: number;
  vapidPublicKey?: string | null;
  vapidPrivateKey?: string | null;
};

export type StoreSettingsTranslation = {
  general?: Partial<GeneralSettings>;
  header?: Partial<HeaderSettings>;
  home?: Partial<HomeSettings>;
  marketing?: Partial<MarketingSettings>;
  footer?: Partial<FooterSettings>;
  pwa?: Partial<PwaSettings>;
};

export type StoreSettings = {
  general: GeneralSettings;
  header: HeaderSettings;
  home: HomeSettings;
  localization: LocalizationSettings;
  checkout: CheckoutSettings;
  customerAccounts: CustomerAccountsSettings;
  email: EmailSettings;
  marketing: MarketingSettings;
  footer: FooterSettings;
  pwa: PwaSettings;
  browserNotifications: BrowserNotificationsSettings;
  navigation?: NavigationSettings;
  updatedAt?: string;
  translations?: Record<string, StoreSettingsTranslation>;
};

export type SaveSettingsOptions = {
  scope?: string | string[];
};

type LoadSettingsOptions = {
  allowCachedFallback?: boolean;
  cacheResult?: boolean;
};

const SCOPED_SETTINGS_KEYS = [
  "navigation",
  "general",
  "header",
  "home",
  "localization",
  "checkout",
  "customerAccounts",
  "email",
  "marketing",
  "footer",
  "pwa",
  "browserNotifications",
] as const;

const TRANSLATED_SETTINGS_KEYS = [
  "general",
  "header",
  "home",
  "marketing",
  "footer",
  "pwa",
] as const;

type ScopedSettingsKey = (typeof SCOPED_SETTINGS_KEYS)[number];
type TranslatedSettingsKey = (typeof TRANSLATED_SETTINGS_KEYS)[number];

const SCOPED_SETTINGS_KEY_MAP = new Map<string, ScopedSettingsKey>(
  SCOPED_SETTINGS_KEYS.map((key) => [key.toLowerCase(), key]),
);
const TRANSLATED_SETTINGS_KEY_SET = new Set<string>(TRANSLATED_SETTINGS_KEYS);

const DEFAULT_SETTINGS: StoreSettings = {
  navigation: { visibility: {} },
  general: {
    storeName: "My Store",
    logoUrl: null,
    faviconUrl: null,
    supportEmail: "",
    supportPhone: "",
    whatsAppNumber: "",
    primaryColor: "#2563EB",
    address: "",
    mapUrl: "",
  },
  header: {
    enabled: true,
    showSearch: true,
    showWishlist: false,
    showCart: true,
    showAccount: true,
    announcementEnabled: false,
    announcementPhone: "",
    announcementEmail: "",
    announcementText: "",
    announcementButtonText: "",
    announcementButtonUrl: "/products",
  },
  home: {
    heroEnabled: true,
    heroSlides: [],
    categoriesEnabled: true,
    categoriesTitle: "Shop by category",
    categoriesButtonText: "View all",
    categoriesButtonUrl: "/products",
    serviceHighlightsEnabled: false,
    serviceHighlights: [],
    partnerLogosEnabled: false,
    partnerLogosTitle: "",
    partnerLogos: [],
    featuredDeal: null,
    recentOrders: null,
    schemaVersion: 2,
    design: {
      preset: "modern",
      secondaryColor: "#10B981",
      accentColor: "#F59E0B",
      backgroundColor: "#FFFFFF",
      foregroundColor: "#111827",
      headingFont: "Outfit",
      bodyFont: "Outfit",
      contentWidth: "standard",
      sectionSpacing: "comfortable",
      buttonStyle: "pill",
      cardStyle: "soft",
      imageRatio: "portrait",
      animationLevel: "subtle",
      mobileProductColumns: 2,
    },
    sections: [
      {
        id: "default-home-hero",
        type: "hero",
        data: { slides: [] },
        enabled: true,
      },
      {
        id: "default-home-features",
        type: "features",
        data: { items: [] },
        enabled: false,
      },
      {
        id: "default-home-categories",
        type: "categories",
        data: {
          title: "Shop by category",
          ctaText: "View all",
          ctaUrl: "/products",
        },
        enabled: true,
      },
      {
        id: "default-home-partners",
        type: "partners",
        data: { title: "", items: [] },
        enabled: false,
      },
      {
        id: "default-home-products",
        type: "product-carousel",
        data: {},
        enabled: true,
      },
      {
        id: "default-home-featured-deal",
        type: "featured-deal",
        data: {},
        enabled: false,
      },
      {
        id: "default-home-recent-orders",
        type: "recent-orders",
        data: {},
        enabled: false,
      },
    ],
  },
  localization: {
    defaultCurrency: "USD",
    supportedCurrencies: ["USD"],
    defaultLanguage: "en",
    languages: ["en"],
    timezone: "UTC",
  },
  checkout: {
    allowGuestCheckout: true,
    requirePhone: false,
    defaultCountry: "",
    allowCountrySelection: true,
    requireLastName: true,
    requireAddressLine1: true,
    requireCity: true,
    requireRegion: true,
    requirePostalCode: true,
    whatsAppCartEnabled: true,
    zeroPriceInquiryEnabled: false,
    taxEnabled: false,
    shippingEnabled: true,
    bankInfo: "",
    bankAccountName: "",
    bankAccountNumber: "",
    bankName: "",
    bankIban: "",
    bankSwift: "",
    enableCard: false,
    enableCod: true,
    enableBankTransfer: false,
  },
  customerAccounts: {
    passwordLoginEnabled: true,
    registrationEnabled: true,
    googleLoginEnabled: true,
  },
  email: {
    fromName: "Store",
    fromEmail: "",
    sendOrderConfirmation: true,
    sendAdminNotification: true,
  },
  marketing: {
    newsletter: {
      enabled: false,
      title: "",
      subtitle: "",
      buttonLabel: "",
      marketingConsentText: "",
      marketingConsentDefault: false,
    },
    pixels: [],
    socialLinks: [],
  },
  footer: {
    enabled: true,
    description: "",
    contactEnabled: false,
    contactTitle: "Contact us",
    copyrightEnabled: true,
    copyrightText: "Copyright {year} {storeName}. All rights reserved.",
    paymentIcons: [],
  },
  pwa: {
    enabled: false,
    name: "StoriZone App",
    shortName: "StoriZone",
    startUrl: "/",
    display: "standalone",
    themeColor: "#000000",
    backgroundColor: "#ffffff",
    pushEnabled: false,
    vapidPublicKey: null,
    vapidPrivateKey: null,
  },
  browserNotifications: {
    enabled: false,
    transactionalEnabled: true,
    promotionalEnabled: false,
    promptEnabled: true,
    promptTitle: "Stay updated",
    promptMessage: "Enable browser notifications for order updates and offers.",
    promptDelaySeconds: 10,
    promptDismissalDays: 30,
    vapidPublicKey: null,
    vapidPrivateKey: null,
  },
};

export const HOMEPAGE_SECTION_TYPES = [
  "hero",
  "features",
  "categories",
  "category-grid",
  "partners",
  "brand-carousel",
  "product-carousel",
  "product-grid",
  "best-sellers",
  "new-arrivals",
  "top-rated",
  "sale-products",
  "featured-deal",
  "promo-banner",
  "countdown-deal",
  "recent-orders",
  "newsletter",
  "content",
  "spacer",
] as const;

const LEGACY_HOMEPAGE_SECTION_TYPES = [
  "hero",
  "features",
  "categories",
  "partners",
  "product-carousel",
  "featured-deal",
  "recent-orders",
] as const;

type HomepageSectionType = (typeof HOMEPAGE_SECTION_TYPES)[number];

function isPageSectionType(value: string): value is HomepageSectionType {
  return (HOMEPAGE_SECTION_TYPES as readonly string[]).includes(value);
}

function createDefaultHomepageSections(
  home: Partial<HomeSettings>,
): PageSection[] {
  return [
    {
      id: "default-home-hero",
      type: "hero",
      data: { slides: Array.isArray(home.heroSlides) ? home.heroSlides : [] },
      enabled: home.heroEnabled ?? true,
    },
    {
      id: "default-home-features",
      type: "features",
      data: {
        items: Array.isArray(home.serviceHighlights)
          ? home.serviceHighlights
          : [],
      },
      enabled: home.serviceHighlightsEnabled ?? false,
    },
    {
      id: "default-home-categories",
      type: "categories",
      data: {
        title: home.categoriesTitle,
        ctaText: home.categoriesButtonText,
        ctaUrl: home.categoriesButtonUrl,
      },
      enabled: home.categoriesEnabled ?? true,
    },
    {
      id: "default-home-partners",
      type: "partners",
      data: {
        title: home.partnerLogosTitle,
        items: Array.isArray(home.partnerLogos) ? home.partnerLogos : [],
      },
      enabled: home.partnerLogosEnabled ?? false,
    },
    {
      id: "default-home-products",
      type: "product-carousel",
      data: {},
      enabled: true,
    },
    {
      id: "default-home-featured-deal",
      type: "featured-deal",
      data: home.featuredDeal ? { ...home.featuredDeal } : {},
      enabled: Boolean(home.featuredDeal?.enabled),
    },
    {
      id: "default-home-recent-orders",
      type: "recent-orders",
      data: home.recentOrders ? { ...home.recentOrders } : {},
      enabled: Boolean(home.recentOrders?.enabled),
    },
  ];
}

export function normalizeHomepageSections(
  home: Partial<HomeSettings>,
): PageSection[] {
  const defaults = createDefaultHomepageSections(home);
  const defaultsByType = new Map(
    defaults.map((section) => [section.type, section]),
  );
  const configured = (Array.isArray(home.sections) ? home.sections : [])
    .filter((section) => section && isPageSectionType(section.type))
    .map((section) => {
      const fallback = defaultsByType.get(section.type);
      return {
        ...(fallback || {}),
        ...section,
        data: {
          ...(fallback?.data && typeof fallback.data === "object"
            ? fallback.data
            : {}),
          ...(section.data && typeof section.data === "object"
            ? section.data
            : {}),
        },
      };
    });

  if (configured.length === 0) return defaults;

  const counts = configured.reduce<Record<string, number>>(
    (result, section) => {
      result[section.type] = (result[section.type] || 0) + 1;
      return result;
    },
    {},
  );
  const isAuthoritative =
    Number(home.schemaVersion || 1) >= 2 ||
    Object.values(counts).some((count) => count > 1) ||
    configured.some(
      (section) =>
        !(LEGACY_HOMEPAGE_SECTION_TYPES as readonly string[]).includes(
          section.type,
        ),
    ) ||
    LEGACY_HOMEPAGE_SECTION_TYPES.every((type) => counts[type] > 0);
  if (isAuthoritative) return configured;

  const configuredByType = new Map(
    configured.map((section) => [section.type, section]),
  );
  return defaults.map(
    (section) => configuredByType.get(section.type) ?? section,
  );
}

export function mergeSettings(
  partial: Partial<StoreSettings> | null | undefined,
): StoreSettings {
  if (!partial) return DEFAULT_SETTINGS;

  const pixels = Array.isArray(partial.marketing?.pixels)
    ? partial.marketing!.pixels
    : DEFAULT_SETTINGS.marketing.pixels;
  const socialLinks = Array.isArray(partial.marketing?.socialLinks)
    ? partial.marketing!.socialLinks
    : DEFAULT_SETTINGS.marketing.socialLinks;
  const paymentIcons = Array.isArray(partial.footer?.paymentIcons)
    ? partial.footer!.paymentIcons
    : DEFAULT_SETTINGS.footer.paymentIcons;
  const heroSlides = Array.isArray(partial.home?.heroSlides)
    ? partial.home!.heroSlides
    : DEFAULT_SETTINGS.home.heroSlides;
  const serviceHighlights = Array.isArray(partial.home?.serviceHighlights)
    ? partial.home!.serviceHighlights
    : DEFAULT_SETTINGS.home.serviceHighlights;
  const partnerLogos = Array.isArray(partial.home?.partnerLogos)
    ? partial.home!.partnerLogos
    : DEFAULT_SETTINGS.home.partnerLogos;
  const mergedHome = partial.home
    ? {
        ...DEFAULT_SETTINGS.home,
        ...partial.home,
        design: partial.home.design
          ? { ...DEFAULT_SETTINGS.home.design!, ...partial.home.design }
          : DEFAULT_SETTINGS.home.design,
        heroSlides,
        serviceHighlights,
        partnerLogos,
        sections: partial.home.sections,
      }
    : DEFAULT_SETTINGS.home;
  const sections = normalizeHomepageSections(mergedHome);

  const legacyBrowserNotifications = partial.pwa?.pushEnabled
    ? {
        ...DEFAULT_SETTINGS.browserNotifications,
        enabled: true,
        vapidPublicKey: partial.pwa.vapidPublicKey,
        vapidPrivateKey: partial.pwa.vapidPrivateKey,
      }
    : DEFAULT_SETTINGS.browserNotifications;

  return {
    ...DEFAULT_SETTINGS,
    ...partial,
    navigation: { visibility: partial.navigation?.visibility ?? {} },
    general: partial.general
      ? { ...DEFAULT_SETTINGS.general, ...partial.general }
      : DEFAULT_SETTINGS.general,
    header: partial.header
      ? { ...DEFAULT_SETTINGS.header, ...partial.header }
      : DEFAULT_SETTINGS.header,
    home: { ...mergedHome, sections },
    localization: partial.localization
      ? { ...DEFAULT_SETTINGS.localization, ...partial.localization }
      : DEFAULT_SETTINGS.localization,
    checkout: partial.checkout
        ? { ...DEFAULT_SETTINGS.checkout, ...partial.checkout }
        : DEFAULT_SETTINGS.checkout,
    customerAccounts: partial.customerAccounts
      ? { ...DEFAULT_SETTINGS.customerAccounts, ...partial.customerAccounts }
      : DEFAULT_SETTINGS.customerAccounts,
    email: partial.email
      ? { ...DEFAULT_SETTINGS.email, ...partial.email }
      : DEFAULT_SETTINGS.email,
    marketing: partial.marketing
      ? {
          ...DEFAULT_SETTINGS.marketing,
          ...partial.marketing,
          newsletter: partial.marketing.newsletter
            ? {
                ...DEFAULT_SETTINGS.marketing.newsletter,
                ...partial.marketing.newsletter,
              }
            : DEFAULT_SETTINGS.marketing.newsletter,
          pixels,
          socialLinks,
        }
      : DEFAULT_SETTINGS.marketing,
    footer: partial.footer
      ? { ...DEFAULT_SETTINGS.footer, ...partial.footer, paymentIcons }
      : DEFAULT_SETTINGS.footer,
    pwa: partial.pwa
      ? { ...DEFAULT_SETTINGS.pwa, ...partial.pwa }
      : DEFAULT_SETTINGS.pwa,
    browserNotifications: partial.browserNotifications
      ? {
          ...DEFAULT_SETTINGS.browserNotifications,
          ...partial.browserNotifications,
        }
      : legacyBrowserNotifications,
  };
}

function loadLocal(): StoreSettings {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;
  const raw = window.localStorage.getItem(KEY);
  if (raw) return mergeSettings(JSON.parse(raw) as Partial<StoreSettings>);
  window.localStorage.setItem(KEY, JSON.stringify(DEFAULT_SETTINGS));
  return DEFAULT_SETTINGS;
}

function saveLocal(settings: StoreSettings) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(KEY, JSON.stringify(settings));
}

function normalizeSaveScopes(
  scope: SaveSettingsOptions["scope"],
): ScopedSettingsKey[] | null {
  const rawScopes = Array.isArray(scope)
    ? scope
    : typeof scope === "string"
      ? scope.split(",")
      : [];
  const scopes = rawScopes
    .map((value) => SCOPED_SETTINGS_KEY_MAP.get(value.trim().toLowerCase()))
    .filter((value): value is ScopedSettingsKey => Boolean(value));

  return scopes.length > 0 ? [...new Set(scopes)] : null;
}

function isTranslatedSettingsKey(
  scope: ScopedSettingsKey,
): scope is TranslatedSettingsKey {
  return TRANSLATED_SETTINGS_KEY_SET.has(scope);
}

function hasOwn(record: object, key: string) {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function isEmptyTranslation(document: StoreSettingsTranslation) {
  return TRANSLATED_SETTINGS_KEYS.every((key) => !document[key]);
}

function mergeScopedTranslations(
  current: StoreSettings["translations"],
  incoming: StoreSettings["translations"],
  scopes: readonly ScopedSettingsKey[],
): StoreSettings["translations"] {
  const translatedScopes = scopes.filter(isTranslatedSettingsKey);
  if (translatedScopes.length === 0) {
    return current;
  }

  const languages = new Set([
    ...Object.keys(current ?? {}),
    ...Object.keys(incoming ?? {}),
  ]);
  const merged: Record<string, StoreSettingsTranslation> = {};

  languages.forEach((language) => {
    const currentDocument = current?.[language] ?? {};
    const incomingDocument = incoming?.[language] ?? {};
    const nextDocument: StoreSettingsTranslation = { ...currentDocument };

    translatedScopes.forEach((scope) => {
      if (!hasOwn(incomingDocument, scope)) {
        return;
      }

      const nextValue = incomingDocument[scope];
      if (nextValue == null) {
        delete nextDocument[scope];
        return;
      }

      nextDocument[scope] = nextValue;
    });

    if (!isEmptyTranslation(nextDocument)) {
      merged[language] = nextDocument;
    }
  });

  return Object.keys(merged).length > 0 ? merged : undefined;
}

function mergeScopedSettings(
  current: StoreSettings,
  incoming: StoreSettings,
  scopes: readonly ScopedSettingsKey[] | null,
): StoreSettings {
  if (!scopes || scopes.length === 0) {
    return incoming;
  }

  const merged: StoreSettings = {
    ...current,
    updatedAt: incoming.updatedAt,
  };

  scopes.forEach((scope) => {
    merged[scope] = incoming[scope] as never;
  });

  merged.translations = mergeScopedTranslations(
    current.translations,
    incoming.translations,
    scopes,
  );

  return merged;
}

export async function loadSettings(
  options: LoadSettingsOptions = {},
): Promise<StoreSettings> {
  const { allowCachedFallback = true, cacheResult = true } = options;

  if (isStubEnabled()) return loadLocal();

  try {
    const res = await adminHttp.get("/api/admin/settings");
    if (res) {
      const merged = mergeSettings(res as Partial<StoreSettings>);
      if (cacheResult) {
        saveLocal(merged);
      }
      return merged;
    }
  } catch (error) {
    if (!allowCachedFallback) {
      throw error;
    }
  }

  if (!allowCachedFallback) {
    throw new Error(
      translateUi(
        "settingsLoadCouldNotBeConfirmed",
        "Settings load could not be confirmed.",
      ),
    );
  }

  return loadLocal();
}

export async function saveSettings(
  settings: StoreSettings,
  options: SaveSettingsOptions = {},
): Promise<StoreSettings> {
  const normalizedSettings = settings;

  if (isStubEnabled()) {
    const submitted = mergeSettings({
      ...normalizedSettings,
      updatedAt: new Date().toISOString(),
    });
    const saved = mergeSettings(
      mergeScopedSettings(
        loadLocal(),
        submitted,
        normalizeSaveScopes(options.scope),
      ),
    );
    saveLocal(saved);
    return saved;
  }

  const payload = normalizedSettings;
  const scopeParam = Array.isArray(options.scope)
    ? options.scope.filter(Boolean).join(",")
    : options.scope;
  const path = scopeParam
    ? `/api/admin/settings?scope=${encodeURIComponent(scopeParam)}`
    : "/api/admin/settings";
  let res: unknown;
  try {
    res = await adminHttp.post(path, payload);
  } catch (error) {
    if ((error as { status?: number })?.status === 409) {
      let confirmedSettings: StoreSettings | undefined;
      try {
        confirmedSettings = await loadSettings({ allowCachedFallback: false });
      } catch {
        // Keep the original conflict visible if the independent read also fails.
      }
      if (confirmedSettings) {
        throw Object.assign(new Error(translateUi("settingsConflictReviewDrafts", "Settings changed in another session. Latest values were loaded and your drafts were kept. Review them before saving again.")), {
          code: "SETTINGS_CONFLICT", confirmedSettings,
        });
      }
    }
    throw error;
  }
  if (!res || typeof res !== "object") {
    throw new Error(
      translateUi(
        "settingsSaveCouldNotBeConfirmed",
        "Settings save could not be confirmed.",
      ),
    );
  }
  const savedFromResponse = mergeSettings(res as Partial<StoreSettings>);
  saveLocal(savedFromResponse);
  return savedFromResponse;
}
